import React, { useState } from 'react';
import { Text, Box, Stack } from 'braid-design-system';
import { isEmail, maxLength, required } from '@seek/validators-js';
import {
  trackCancelClickedEvent,
  trackContinueClickedEvent,
  trackEditEmailClickedEvent,
} from '../../../utils/events/addCandidateEvents';
import PreviouslyAppliedMessage, {
  type MostRecentApplication,
} from './utils/PreviouslyAppliedMessage';
import isPhone from './utils/isPhone';
import type { IJobApplicationStore } from '../../../stores/JobApplicationStore';
import { logError } from '../../../utils/logError';
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import StepThree from './StepThree';
import StepFour from './StepFour';
import ActionButtons from './ActionButtons';
import {
  ADD_CANDIDATE_FORM_FIELD_NAMES,
  type AddCandidate,
  type FormValues,
} from './AddCandidateForm';
import { ADD_CANDIDATE_FORM_STEP, type CandidateAttachment } from './types';
import {
  ACCEPTED_ADD_CANDIDATE_FILE_FORMAT,
  ADD_CANDIDATE_MAX_FILE_SIZE,
  validateFileFormat,
  validateFileNameChars,
  validateFileSize,
} from '../../../utils/fileValidator';
import { NOTES_MAX_CONTENT_LENGTH } from '../../details/Notes';
import translations from './.vocab/';
import { useTranslations } from '@vocab/react';

interface Props {
  handleSubmit?: any;
  setFieldValue: any;
  resetForm: () => void;
  addCandidate: AddCandidate;
  jobApplicationStore: IJobApplicationStore;
  useField: any;
  onClose?(): void;
}
const AddCandidateFormFields = ({
  handleSubmit,
  resetForm,
  addCandidate,
  setFieldValue,
  jobApplicationStore,
  useField,
  onClose,
}: Props) => {
  const [currentStep, setCurrentStep] = useState<ADD_CANDIDATE_FORM_STEP>(
    ADD_CANDIDATE_FORM_STEP.ONE,
  );

  const { t } = useTranslations(translations);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [resume, setResume] = useState<CandidateAttachment>({
    previousAttachment: null,
    file: null,
    isValid: true,
  });
  const [coverLetter, setCoverLetter] = useState<CandidateAttachment>({
    previousAttachment: null,
    file: null,
    isValid: true,
  });

  const [mostRecentApplication, setMostRecentApplication] =
    useState<MostRecentApplication | null>(null);

  const email = useField({
    id: ADD_CANDIDATE_FORM_FIELD_NAMES.EMAIL,
    validators: [required, isEmail],
    validatorProps: {
      requiredLabel: t('Email address').toLocaleLowerCase(),
    },
  });
  const firstName = useField({
    id: ADD_CANDIDATE_FORM_FIELD_NAMES.FIRST_NAME,
    validators: [required],
    validatorProps: {
      requiredLabel: t('First name').toLocaleLowerCase(),
    },
  });
  const lastName = useField({
    id: ADD_CANDIDATE_FORM_FIELD_NAMES.LAST_NAME,
    validators: [required],
    validatorProps: {
      requiredLabel: t('Last name').toLocaleLowerCase(),
    },
  });
  const phone = useField({
    id: ADD_CANDIDATE_FORM_FIELD_NAMES.PHONE,
    validators: [required, isPhone],
    validatorProps: {
      requiredLabel: t('Phone number').toLocaleLowerCase(),
    },
  });
  const mostRecentJobTitle = useField({
    id: ADD_CANDIDATE_FORM_FIELD_NAMES.MOST_RECENT_JOB_TITLE,
  });
  const mostRecentCompanyName = useField({
    id: ADD_CANDIDATE_FORM_FIELD_NAMES.MOST_RECENT_COMPANY_NAME,
  });
  const timeInRoleYears = useField({
    id: ADD_CANDIDATE_FORM_FIELD_NAMES.TIME_IN_ROLE_YEARS,
  });
  const timeInRoleMonths = useField({
    id: ADD_CANDIDATE_FORM_FIELD_NAMES.TIME_IN_ROLE_MONTHS,
  });
  const notes = useField({
    id: ADD_CANDIDATE_FORM_FIELD_NAMES.NOTES,
    validators: [maxLength] as any,
    validatorProps: { max: NOTES_MAX_CONTENT_LENGTH },
  });
  const originId = useField({
    id: ADD_CANDIDATE_FORM_FIELD_NAMES.ORIGIN_ID,
  });

  const onSave = async (formValues: FormValues) => {
    setErrorMessage('');
    try {
      setIsLoading(true);
      const candidateFormValues = {
        ...formValues,
        resume,
        coverLetter,
      };
      await addCandidate(candidateFormValues);
      onClose?.();
    } catch (error: any) {
      setErrorMessage(
        t(
          'Sorry, we had an issue adding this candidate. Please try again later.',
        ),
      );
      logError(
        {
          errorCode: 'MANUALLY_ADD_CANDIDATE',
          description: 'Failed to save manually added candidate',
        },
        {
          error,
        },
      );
    }
    setIsLoading(false);
  };

  const nextStep = () => {
    let step: number = currentStep;
    step =
      step >= ADD_CANDIDATE_FORM_STEP.THREE
        ? ADD_CANDIDATE_FORM_STEP.FOUR
        : step + 1;
    setCurrentStep(step);
  };

  const checkEmail = async (evt: any) => {
    evt.preventDefault();
    // autofocus doesn't work in dialog so we manually trigger email blur to validate.
    email.onBlur();
    setErrorMessage('');
    if (email.value !== '' && email.valid) {
      try {
        setIsLoading(true);

        const details = await jobApplicationStore.checkPreviousAppsByEmail(
          email.value,
        );

        if (details?.mostRecentApplication && details?.candidateDetails) {
          setFieldValue(
            ADD_CANDIDATE_FORM_FIELD_NAMES.FIRST_NAME,
            details.candidateDetails.firstName,
          );
          setFieldValue(
            ADD_CANDIDATE_FORM_FIELD_NAMES.LAST_NAME,
            details.candidateDetails.lastName,
          );
          setFieldValue(
            ADD_CANDIDATE_FORM_FIELD_NAMES.PHONE,
            details.candidateDetails.phone,
          );
          setFieldValue(
            ADD_CANDIDATE_FORM_FIELD_NAMES.MOST_RECENT_JOB_TITLE,
            details.candidateDetails.mostRecentJobTitle || '',
          );
          setFieldValue(
            ADD_CANDIDATE_FORM_FIELD_NAMES.MOST_RECENT_COMPANY_NAME,
            details.candidateDetails.mostRecentCompanyName || '',
          );
          setFieldValue(
            ADD_CANDIDATE_FORM_FIELD_NAMES.TIME_IN_ROLE_YEARS,
            details.candidateDetails.timeInRoleYears || '',
          );

          setFieldValue(
            ADD_CANDIDATE_FORM_FIELD_NAMES.TIME_IN_ROLE_MONTHS,
            details.candidateDetails.timeInRoleMonths || '',
          );
          setMostRecentApplication(details.mostRecentApplication);
          setResume({
            ...resume,
            previousAttachment: details.candidateDetails.previousResume,
          } as CandidateAttachment);
          setCoverLetter({
            ...coverLetter,
            previousAttachment: details.candidateDetails.previousCoverLetter,
          } as CandidateAttachment);
        }

        trackContinueClickedEvent(currentStep);
        nextStep();
      } catch (error: any) {
        setErrorMessage(
          t(
            'Sorry, we had an issue checking this email address. Please try again later.',
          ),
        );
        logError(
          {
            errorCode: 'MANUALLY_ADD_CANDIDATE_CHECK_EMAIL',
            description:
              'Failed to check the email address when manually adding a candidate',
          },
          {
            error,
          },
        );
      }
      setIsLoading(false);
    }
  };

  const isValidAttachment = (file: File | null) => {
    if (file === null) {
      return true;
    }
    return (
      validateFileFormat(
        file.name,
        file.type,
        ACCEPTED_ADD_CANDIDATE_FILE_FORMAT,
      ) &&
      validateFileNameChars(file.name) &&
      validateFileSize(file.size, ADD_CANDIDATE_MAX_FILE_SIZE)
    );
  };

  const validateAttachments = () => {
    const validResume = isValidAttachment(resume.file);
    const validCoverLetter = isValidAttachment(coverLetter.file);
    setResume({ ...resume, isValid: validResume });
    setCoverLetter({ ...coverLetter, isValid: validCoverLetter });
    return validResume && validCoverLetter;
  };

  const onSubmit = handleSubmit(async (formValues: FormValues) => {
    trackContinueClickedEvent(currentStep);
    if (currentStep >= ADD_CANDIDATE_FORM_STEP.FOUR && validateAttachments()) {
      await onSave(formValues);
    } else {
      nextStep();
    }
  });

  const handleCancel = () => {
    setIsLoading(false);
    trackCancelClickedEvent({
      pageName: `add-candidate:step${currentStep}`,
    });
    setCurrentStep(1);
    resetForm();
    onClose?.();
  };

  const onEditEmail = () => {
    trackEditEmailClickedEvent();
    resetForm();
    setCurrentStep(1);
    setMostRecentApplication(null);
  };

  return (
    <Box background="surface" paddingTop="small">
      <form
        onSubmit={
          currentStep > ADD_CANDIDATE_FORM_STEP.ONE ? onSubmit : checkEmail
        }
        onReset={resetForm}
      >
        <Stack space="medium">
          {mostRecentApplication ? (
            <PreviouslyAppliedMessage
              lastName={lastName.value}
              firstName={firstName.value}
              mostRecentApplication={mostRecentApplication}
            />
          ) : (
            <Text>{t('Enter the details of candidate...')}</Text>
          )}

          <StepOne
            email={email}
            editable={currentStep === ADD_CANDIDATE_FORM_STEP.ONE}
            showEditButton={currentStep === ADD_CANDIDATE_FORM_STEP.TWO}
            onEditEmail={onEditEmail}
          />

          {currentStep >= ADD_CANDIDATE_FORM_STEP.TWO && (
            <StepTwo
              editable={currentStep === ADD_CANDIDATE_FORM_STEP.TWO}
              firstName={firstName}
              lastName={lastName}
              phone={phone}
            />
          )}

          {currentStep >= ADD_CANDIDATE_FORM_STEP.THREE && (
            <StepThree
              editable={currentStep === ADD_CANDIDATE_FORM_STEP.THREE}
              mostRecentJobTitle={mostRecentJobTitle}
              mostRecentCompanyName={mostRecentCompanyName}
              timeInRoleYears={timeInRoleYears}
              timeInRoleMonths={timeInRoleMonths}
            />
          )}

          {currentStep >= ADD_CANDIDATE_FORM_STEP.FOUR && (
            <StepFour
              notes={notes}
              originId={originId}
              setResume={setResume}
              resume={resume}
              setCoverLetter={setCoverLetter}
              coverLetter={coverLetter}
            />
          )}
          <ActionButtons
            buttonText={
              currentStep === ADD_CANDIDATE_FORM_STEP.FOUR
                ? 'Add Candidate'
                : 'Continue'
            }
            handleCancel={handleCancel}
            isLoading={isLoading}
          />
        </Stack>
      </form>
      {errorMessage && errorMessage !== '' && (
        <Box marginTop="small" paddingY="small">
          <Text tone="critical">{errorMessage}</Text>
        </Box>
      )}
    </Box>
  );
};

export default AddCandidateFormFields;
